import { Injectable } from '@angular/core';
import { ActivationCodeSource, LicenseStatus } from '@uigy/generated/graphql';
import { ActivationCodeDialogComponent } from '@uigy/modules/license/activation-code-dialog/activation-code-dialog.component';
import { CertificateDialogComponent } from '@uigy/modules/license/certificate-dialog/certificate-dialog.component';
import { LicenseCodeDialogComponent } from '@uigy/modules/license/license-code-dialog/license-code-dialog.component';
import { LicenseManagerService } from '@uigy/modules/license/license-manager.service';
import { SliderSizeEnumV2 } from '@uigy/shared/components/slider-v2/enums/slider-v2.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { WizardPage } from '@uigy/shared/components/wizard/models/wizard-page';
import { WizardService } from '@uigy/shared/components/wizard/services/wizard.service';
import { LanguageService } from '@uigy/shared/services/language.service';
import { PopupService } from '@uigy/shared/services/popup.service';
import { Observable } from 'rxjs';
import { pluck, switchMap } from 'rxjs/operators';

@Injectable()
export class LicenseDialogsService {

  constructor(
    private licenseManagerService: LicenseManagerService,
    private wizardService: WizardService,
    private languageService: LanguageService,
    private toasterService: ToasterService,
    private popupService: PopupService,
  ) { }

  public openLicenseCodeDialog(): Observable<string | undefined> {
    const stepTexts = this.languageService.texts.license_activation.step1;

    return this.wizardService.openWizardSlider<void, {code: string}>({
      header: stepTexts.slider.title,
      submitBtnText: stepTexts.slider.primary_btn,
      overlay: SliderSizeEnumV2.form,
      pages: [new WizardPage(LicenseCodeDialogComponent)],
    }).pipe(
      switchMap(({code}) => this.licenseManagerService.registerLicenseCode(code)),
      this.toasterService.toasterOperator(stepTexts.messages),
    );
  }

  public openLicenseCodePopup(): Observable<string | undefined> {
    const popupTexts = this.languageService.texts.license_activation.step1.reenter_dialog;

    return new Observable<string | undefined>(subscriber => {
      this.popupService.openPopup({
        header: popupTexts.title,
        text: popupTexts.content,
        submit: {
          title: popupTexts.primary_btn,
          execute: () => this.openLicenseCodeDialog().subscribe(subscriber),
        },
        cancel: {
          title: this.languageService.texts.general.buttons.cancel,
          execute: () => subscriber.complete(),
        },
      });
    });
  }

  public openCertificateDialog(activationCodeSource: ActivationCodeSource): Observable<void> {
    const stepTexts = this.languageService.texts.license_activation.step2;

    return this.wizardService.openWizardSlider<{ activationCodeSource: ActivationCodeSource }, void>({
      header: stepTexts.slider.title,
      submitBtnText: stepTexts.slider.primary_btn,
      suppressCancelButton: true,
      overlay: SliderSizeEnumV2.form,
      pages: [new WizardPage(CertificateDialogComponent)],
      data: { activationCodeSource }
    });
  }

  public openActivationCodeDialog(): Observable<LicenseStatus | null | undefined> {
    const stepTexts = this.languageService.texts.license_activation.step3;

    return this.wizardService.openWizardSlider<void, { code: string }>({
      header: stepTexts.slider.title,
      submitBtnText: stepTexts.slider.primary_btn,
      overlay: SliderSizeEnumV2.form,
      pages: [new WizardPage(ActivationCodeDialogComponent)],
    }).pipe(
      pluck('code'),
      switchMap(cert => this.licenseManagerService.activateLicense(cert)),
      this.toasterService.toasterOperator(stepTexts.messages),
    );
  }
}
